import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock3, Tag } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { INSIGHTS, getInsight } from "@/data/insights";
import { PHONE, PHONE_HREF, EMAIL_HREF } from "@/data";
import NotFound from "./NotFound";

const fmt = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

const InsightArticle = () => {
  const { slug } = useParams();
  const article = getInsight(slug);
  if (!article) return <NotFound />;
  const related = INSIGHTS.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`insight-article-${article.slug}`}>
      <Seo
        title={`${article.title} | Xtreme Protection Wales`}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: { "@type": "Organization", name: "Xtreme Protection — Wales Security Services" },
        }}
      />

      <section className="border-b border-[#1C2027] bg-[#0D1015] pb-14 pt-32">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Link to="/insights" data-testid="article-back-link" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#F5A800] transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All Insights
          </Link>
          <Reveal>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">
              <span className="flex items-center gap-1.5 text-[#F5A800]"><Tag className="h-3 w-3" />{article.category}</span>
              <span>{fmt(article.date)}</span>
              <span className="flex items-center gap-1.5"><Clock3 className="h-3 w-3" />{article.read} min read</span>
            </div>
            <h1 className="font-condensed mt-4 text-4xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl" data-testid="article-title">{article.title}</h1>
            <p className="mt-5 text-base leading-relaxed text-[#C8CCD2] sm:text-lg">{article.excerpt}</p>
          </Reveal>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 pt-10 sm:px-6">
        <Reveal>
          <div className="clip-frame relative">
            <img src={article.image} alt={article.title} className="h-72 w-full object-cover sm:h-96" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10]/60 to-transparent" />
          </div>
        </Reveal>
      </div>

      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-6" data-testid="article-body">
        {article.sections.map((s, i) => (
          <Reveal key={i} delay={0.05} y={16}>
            <section className="mb-10">
              <h2 className="font-condensed flex items-center gap-3 text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
                <span className="text-sm font-bold text-[#F5A800]">{String(i + 1).padStart(2, "0")}</span>
                {s.heading}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-[#9BA1AB]">{s.text}</p>
            </section>
          </Reveal>
        ))}

        <Reveal>
          <div className="border border-[#F5A800]/30 bg-[#F5A800]/5 p-6 sm:p-8" data-testid="article-cta">
            <p className="font-condensed text-2xl font-bold uppercase tracking-tight text-white">Need professional security in Wales?</p>
            <p className="mt-2 text-sm leading-relaxed text-[#9BA1AB]">
              Free risk assessment, honest recommendations and SIA-licensed officers across 35+ Welsh towns and cities.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href={PHONE_HREF} data-testid="article-call-btn" className="bg-[#F5A800] px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-[#0A0C10] transition-colors duration-300 hover:bg-white">
                Call {PHONE}
              </a>
              <a href={EMAIL_HREF} data-testid="article-email-btn" className="border border-[#F5A800]/50 px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-[#F5A800] transition-colors duration-300 hover:bg-[#F5A800] hover:text-[#0A0C10]">
                Email Us
              </a>
            </div>
          </div>
        </Reveal>
      </article>

      <section className="border-t border-[#1C2027] bg-[#0D1015] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h2 className="font-condensed text-3xl font-bold uppercase tracking-tight text-white">Further briefings</h2>
          <div className="mt-8 grid gap-px border border-[#1C2027] bg-[#1C2027] sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/insights/${r.slug}`} data-testid={`related-insight-${r.slug}`} className="group flex h-full flex-col justify-between bg-[#10131A] p-6 transition-colors duration-300 hover:bg-[#161A22]">
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-condensed text-lg font-bold uppercase leading-tight tracking-wide text-white transition-colors duration-300 group-hover:text-[#F5A800]">{r.title}</h3>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-white/30 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#F5A800]" />
                  </div>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#F5A800]/70">{r.category}</p>
                </div>
                <span className="mt-5 text-[10px] font-bold uppercase tracking-[0.24em] text-white/40 group-hover:text-[#F5A800]">{fmt(r.date)}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightArticle;
